import { ActionTree } from 'vuex'
import * as types from './mutation-types'
import RootState from '@vue-storefront/core/types/RootState'
import PartnersState from '../../types/PartnersState'
import rootStore from '@vue-storefront/core/store'
import { processURLAddress, isServer } from '@vue-storefront/core/helpers'
import { Logger } from '@vue-storefront/core/lib/logger'
import fetch from 'isomorphic-fetch'
import config from 'config'
import { partnersListScope } from '../../helpers/listScope'

let ensureSeq = 0

const markPartial = (partners, include) => include && include.length && Array.isArray(partners)
  ? partners.map(p => ({ ...p, _partial: true }))
  : partners

const actions: ActionTree<PartnersState, RootState> = {
  changeCurrentPartner ({ state, commit }, payload) {
    if (!state.partners.find(p => p.code === payload)) {
      throw new Error('Partner with code ' + payload + ' not loaded')
    }
    commit(types.SET_CURRENT_PARTNER_CODE, payload)
  },
  /**
   * Makes the route's partner current, loading only that partner (full list as a
   * fallback) - keeps SSR and its serialized state small, and covers client
   * navigation after a cold landing, when only the landed-on partner is loaded.
   * List views store trimmed (`_partial`) partner docs, which don't count as loaded.
   * Never throws.
   */
  async ensureCurrentPartner ({ state, getters, dispatch }, { code, locationCode }) {
    if (!code) {
      return
    }
    const isFull = p => p.code === code && !p._partial && (!locationCode || p.location_code === locationCode)
    try {
      await rootStore.dispatch('locations/ensureLocation', locationCode)
    } catch (err) {
      Logger.error(err, 'sync')()
    }
    const current = getters.getCurrentPartner
    if (!isServer && current && isFull(current)) {
      return
    }
    const seq = ++ensureSeq
    try {
      if (!state.partners.some(isFull)) {
        await dispatch('loadOnePartner', { code, locationCode })
      }
      if (!state.partners.some(isFull)) {
        throw new Error('Partner with code ' + code + ' not found')
      }
    } catch (err) {
      Logger.error(err, 'sync')()
      try {
        await dispatch('loadPartners', { locationCode })
      } catch (error) {
        Logger.error(error, 'sync')()
        return
      }
    }
    // A later navigation started its own ensure while this one was loading.
    if (!isServer && seq !== ensureSeq) {
      return
    }
    try {
      await dispatch('changeCurrentPartner', code)
    } catch (error) {
      Logger.error(error, 'sync')()
    }
  },
  clearPartners ({ commit }) {
    commit(types.SET_PARTNERS, [])
    commit(types.SET_LIST_SCOPE, null)
  },
  async loadPartners ({ commit, state, dispatch }, payload = { categoryId: null, locationCode: null, from: 0, size: 1000, tag: null, include: [] }) {
    let url = processURLAddress(`${config.partners.base_endpoint}/list/partners`)
    let locationCode = payload.locationCode
    if (!locationCode) {
      let activeLocation = rootStore.getters['locations/getActiveLocation']
      if (activeLocation) {
        locationCode = activeLocation.code
      }
    }
    let char = '?'
    if (locationCode) {
      url = `${url}${char}locationCode=${locationCode}`
      char = '&'
    }
    if (payload.categoryId) {
      url = `${url}${char}categoryId=${payload.categoryId}`
      char = '&'
    }
    if (payload.from !== undefined && payload.size) {
      url = `${url}${char}from=${payload.from}&size=${payload.size}`
      char = '&'
    }
    if (payload.tag && payload.tag.hasOwnProperty('entity_id')) {
      url = `${url}${char}tagId=${payload.tag.entity_id}`
      char = '&'
    }
    if (payload.include && payload.include.length) {
      payload.include.forEach((incl, i) => {
        url += i === 0 ? `&_source_includes=${incl}` : `,${incl}`
      })
    }
    return await fetch(url, { method: config.elasticsearch.queryMethod,
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then(resp => resp.json())
      .then(response => {
        // vsf-api's /list/partners already returns the whole matching set sorted
        // (open desc, priority desc) and sliced to [from, from+size) - no client-side
        // resort needed, and pages append in the already-correct global order.
        const partners = markPartial(response.result, payload.include)
        if (!payload.from || payload.from === 0) {
          commit(types.SET_PARTNERS, partners)
          commit(types.SET_LIST_SCOPE, partnersListScope({
            locationCode,
            categoryId: payload.categoryId,
            tagId: payload.tag && payload.tag.entity_id
          }))
        } else {
          commit(types.ADD_PARTNERS, partners)
        }
        return response
      })
      .catch(error => {
        throw new Error('FetchError in request to ES: ' + error.toString())
      })
  },
  async partnersCount({ commit, state, dispatch }, payload) {
    let url = processURLAddress(`${config.partners.base_endpoint}/list/partners/count`);
    if (payload.categoryId) {
      url = `${url}?categoryId=${payload.categoryId}`
    }
    if (payload.locationCode) {
      let char = payload.categoryId ? '&' : '?'
      url = `${url}${char}locationCode=${payload.locationCode}`
    }

    return await fetch(url, {
      method: config.elasticsearch.queryMethod,
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then(resp => resp.json())
      .then(response => {
        return response;
      })
      .catch(error => {
        throw new Error('FetchError in request to ES: ' + error.toString());
      });
  },
  async categorySeoStats ({ commit, state, dispatch }, payload) {
    let url = processURLAddress(`${config.partners.base_endpoint}/list/partners/category-seo-stats`);
    let char = '?'
    if (payload.categoryId) {
      url = `${url}${char}categoryId=${payload.categoryId}`
      char = '&'
    }
    if (payload.locationCode) {
      url = `${url}${char}locationCode=${payload.locationCode}`
      char = '&'
    }
    if (payload.tagId) {
      url = `${url}${char}tagId=${payload.tagId}`
    }

    return fetch(url, {
      method: config.elasticsearch.queryMethod,
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then(resp => resp.json())
      .then(response => {
        if (response && response.code === 200 && response.result) {
          // `loaded` distinguishes "fetched, genuinely zero partners" from
          // "not fetched yet / fetch failed" so callers don't mistake a
          // stats-endpoint outage for a real empty category (e.g. noindexing
          // a page that actually has content, just because the enhancement
          // endpoint hiccuped).
          commit(types.SET_CATEGORY_SEO_STATS, { ...response.result, loaded: true })
        }
        return response;
      })
      .catch(error => {
        throw new Error('FetchError in request to ES: ' + error.toString());
      });
  },
  async loadAllPartners ({ commit, state }, { include = [] } = {}) {
    let url = processURLAddress(`${config.partners.base_endpoint}/list/partners`)
    if (include.length) {
      url += `?_source_includes=${include.join(',')}`
    }
    await fetch(url, { method: config.elasticsearch.queryMethod,
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then(resp => resp.json())
      .then(response => {
        // already sorted server-side, see loadPartners above
        commit(types.SET_PARTNERS, markPartial(response.result, include))
        commit(types.SET_LIST_SCOPE, null)
        return response
      })
      .catch(error => {
        throw new Error('FetchError in request to ES: ' + error.toString())
      })
  },
  loadOnePartner ({ commit }, payload) {
    let locationCode = payload.locationCode
    if (!locationCode) {
      let activeLocation = rootStore.getters['locations/getActiveLocation']
      if (activeLocation) {
        locationCode = activeLocation.code
      }
    }
    let url = processURLAddress(`${config.partners.base_endpoint}/get/partner?code=${encodeURIComponent(payload.code)}&locationCode=${encodeURIComponent(locationCode || '')}`)
    return fetch(url, { method: config.elasticsearch.queryMethod,
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then(resp => resp.json())
      .then(response => {
        if (response && response.result && response.result.length) {
          commit(types.ADD_PARTNERS, response.result)
          return response.result
        }
      })
      .catch(error => {
        throw new Error('FetchError in request to ES: ' + error.toString())
      })
  },
  resetCurrentPartner ({ commit }) {
    commit(types.RESET_CURRENT_PARTNER_CODE)
  },
  async filterPartner({ commit }, {filter, categoryId, locationCode}) {
    let url = processURLAddress(`${config.partners.base_endpoint}/filter/partners?filter=${filter}&locationCode=${locationCode}&categoryId=${categoryId}`)
    await fetch (url,{ method: config.elasticsearch.queryMethod,
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then(resp => resp.json())
      .then(response => {
        if (response.code === 200) {
          commit(types.SET_FILTER_PARTNER, response.result)
        }
        return response.result
      })
  },
  clearFilterPartners({commit}) {
    commit(types.SET_HIDDEN_FILTER_PARTNERS)
  },
  onOrOffLoader ({commit}, value) {
    commit(types.ON_OR_OFF_LOADER, value)
  }
}

export default actions
